import {getTestProps} from '@benchling/js-test-utils';
import {groupBy} from '@benchling/js-utils';
import {Moment} from 'moment';
import React from 'react';
import _ from 'underscore';

import {
  CalendarEntry,
  CalendarFile,
  CalendarProject,
  CalendarRequest,
  ProjectColorMapping,
} from 'calendar/CalendarConstants';
import CalendarUtil from 'calendar/CalendarUtil';
import DataConnector from 'connect/DataConnector';
import * as nyc from 'ui-library/NYC';

import {ProcessTaskForCalendarGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskForCalendarGraphQL';
import CalendarStore from './CalendarStore';
import DailyFileActivity from './DailyFileActivity';
import DailyProjectActivity from './DailyProjectActivity';

interface Props {
  date: Moment;
  isLoadingData: boolean;
  projects: Array<CalendarProject>;
  entries: Array<CalendarEntry>;
  files: Array<CalendarFile>;
  requests: Array<CalendarRequest>;
  processTasks: Array<ProcessTaskForCalendarGraphQL>;
}

interface EnhancedProps {
  projectColorMapping: ProjectColorMapping;
}

class CalendarDayConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [CalendarStore];

  enhanceProps(): EnhancedProps {
    return {
      projectColorMapping: this.calendarStore().getProjectColorMapping(),
    };
  }
}

class CalendarDay extends React.PureComponent<Props & EnhancedProps> {
  renderProjects(): React.ReactNode {
    if (
      !CalendarUtil.hasCalendarItems(
        this.props.entries,
        this.props.files,
        this.props.requests,
        this.props.processTasks
      )
    ) {
      return (
        <div
          className="calendarDay-noActivity"
          {...getTestProps({component: 'CalendarDay', element: 'noActivity'})}
        >
          No activity was found on {this.props.date.format('dddd, MMMM D, YYYY')}.
        </div>
      );
    }

    const groupedEntries = groupBy(this.props.entries, (entry) => entry.source.id);

    const filesWithSource = this.props.files.filter((file) => file.source != null);
    const groupedFiles = groupBy(filesWithSource, (file) => file.source!.id);

    const groupedRequests = groupBy(this.props.requests, (request) => request.projectId);
    const groupedProcessTasks = groupBy(this.props.processTasks, (task) => task.process.folder.source.id);

    // Show projects that have entries first, then projects that have requests, then projects that have files
    const projects = _.sortBy(this.props.projects, (project) => {
      const projectHasEntries = groupedEntries[project.id] != null;
      const projectHasFiles = groupedFiles[project.id] != null;
      const projectHasRequests = groupedRequests[project.id] != null;
      const projectHasTasks = groupedProcessTasks[project.id] != null;

      if (projectHasEntries) {
        return projectHasRequests ? 0 : 1;
      } else if (projectHasRequests) {
        return projectHasTasks ? 2 : 3;
      } else if (projectHasTasks) {
        return projectHasFiles ? 4 : 5;
      }
      return projectHasFiles ? 6 : 7;
    });

    return projects.map((project) => {
      const projectColor = this.props.projectColorMapping[project.id];
      const entries = groupedEntries[project.id] ?? [];
      const files = groupedFiles[project.id] ?? [];
      const requests = groupedRequests[project.id] ?? [];
      const processTasks = groupedProcessTasks[project.id] ?? [];

      return (
        <div key={project.id}>
          <div
            className="calendarDay-projectName"
            {...getTestProps({component: 'CalendarDay', element: 'projectName'})}
          >
            <div
              style={{backgroundColor: projectColor}}
              className="calendarDay-projectCircle"
              {...getTestProps({component: 'CalendarDay', element: 'projectCircle'})}
            />
            {project.name}
          </div>
          <nyc.Row className="calendarDay-projectContainer">
            <nyc.Col lg={15}>
              <DailyProjectActivity
                date={this.props.date}
                entries={entries}
                requests={requests}
                processTasks={processTasks}
                isLoadingData={this.props.isLoadingData}
              />
            </nyc.Col>
            <nyc.Col lg={7}>
              <DailyFileActivity className="dailyFileActivity--dayView" compact={false} files={files} />
            </nyc.Col>
          </nyc.Row>
        </div>
      );
    });
  }

  override render(): JSX.Element {
    return <div {...getTestProps({component: 'CalendarDay', element: 'root'})}>{this.renderProjects()}</div>;
  }
}

export default CalendarDayConnector.connect(CalendarDay);
